'use client';

import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';

import { useSubscriptionQuery } from '../api/billingApi';
import { useCheckout } from '../hooks/useCheckout';
import { usePlans } from '../hooks/usePlans';
import type { CheckoutResponse, Interval, Tier } from '../types/billing.types';

interface CryptoCheckoutFlowProps {
  tier: Exclude<Tier, 'free' | 'starter'>;
  interval: Interval;
}

/**
 * Crypto checkout — creates a NOWPayments invoice and renders the pay-to
 * details inline (no redirect, since NOWPayments hosts the actual payment
 * page and we want the pay address visible immediately for users who'd
 * rather scan from their Binance/Coinbase mobile app).
 */
export function CryptoCheckoutFlow({ tier, interval }: CryptoCheckoutFlowProps) {
  const { data: plans } = usePlans();
  const { startCheckout, isLoading } = useCheckout();
  const [result, setResult] = useState<CheckoutResponse | null>(null);
  const enabled = Boolean(plans?.crypto_enabled);

  if (!enabled) {
    return (
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 8,
          border: '1px solid var(--pm-rule)',
          color: 'var(--pm-ink-3)',
          fontSize: 13,
        }}
      >
        Crypto payments aren&apos;t enabled in this environment.
      </div>
    );
  }

  if (result) {
    return <CryptoInvoiceView result={result} onRestart={() => setResult(null)} />;
  }

  return (
    <button
      type="button"
      disabled={isLoading}
      onClick={async () => {
        const response = await startCheckout({
          tier,
          interval,
          provider: 'nowpayments',
        });
        setResult(response);
      }}
      style={{
        padding: '10px 16px',
        borderRadius: 9,
        border: '1px solid var(--pm-rule-strong)',
        background: 'var(--pm-paper)',
        color: 'var(--pm-ink)',
        fontSize: 13.5,
        fontWeight: 500,
        cursor: isLoading ? 'progress' : 'pointer',
        opacity: isLoading ? 0.6 : 1,
      }}
    >
      {isLoading ? 'Creating invoice…' : 'Pay with crypto'}
    </button>
  );
}

/**
 * The invoice view — QR + amount + address + live countdown + status.
 *
 * Once an invoice is on screen we poll `/billing/subscription` every 6
 * seconds. The NOWPayments webhook activates the subscription as soon as
 * the on-chain payment is confirmed; the poll picks that up and flips the
 * UI to the "paid" success state.
 */
function CryptoInvoiceView({
  result,
  onRestart,
}: {
  result: CheckoutResponse;
  onRestart: () => void;
}) {
  const { data: sub, refetch } = useSubscriptionQuery(undefined, {
    pollingInterval: 6_000,
  });

  // Show the success state when the subscription is active and not
  // a leftover trial — webhook has landed.
  const activated =
    sub != null && sub.status === 'active' && sub.tier !== 'free';

  if (activated) {
    return <CryptoPaidView sub={sub} />;
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 20,
        padding: 18,
        borderRadius: 14,
        border: '1px solid var(--pm-rule)',
        background: 'var(--pm-paper)',
      }}
    >
      <QrPanel result={result} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
        <Header expiresAt={result.expires_at} />
        <NetworkBlock currency={result.pay_currency} />
        <AmountBlock result={result} />
        <AddressBlock address={result.pay_address} />
        <BinanceTips currency={result.pay_currency} />
        <WaitingNotice onRefresh={() => void refetch()} />
        {result.checkout_url && (
          <a
            href={result.checkout_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 12,
              color: 'var(--pm-ink-3)',
              textDecoration: 'underline',
            }}
          >
            Open hosted payment page →
          </a>
        )}
        <button
          type="button"
          onClick={onRestart}
          style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            color: 'var(--pm-ink-4)',
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          Cancel + start over
        </button>
      </div>
    </div>
  );
}

function QrPanel({ result }: { result: CheckoutResponse }) {
  const uri = buildWalletUri(result);
  if (!result.pay_address) return null;
  return (
    <div
      style={{
        width: 200,
        background: '#fff',
        border: '1px solid var(--pm-rule)',
        borderRadius: 12,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <QRCodeSVG value={uri ?? result.pay_address} size={168} level="M" />
      <div
        style={{
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: 'var(--pm-ink-4)',
          fontWeight: 600,
        }}
      >
        Scan from your wallet
      </div>
    </div>
  );
}

/**
 * Returns a BIP-21 style URI when we can build one (BTC, ETH, LTC), or
 * falls back to the raw address (USDT/USDC and others, where wallet
 * URI schemes are inconsistent across chains).
 */
function buildWalletUri(result: CheckoutResponse): string | null {
  const cur = (result.pay_currency ?? '').toLowerCase();
  const amount = result.pay_amount;
  const address = result.pay_address;
  if (!address) return null;
  const SCHEME: Record<string, string> = {
    btc: 'bitcoin',
    eth: 'ethereum',
    ltc: 'litecoin',
  };
  const scheme = SCHEME[cur];
  if (!scheme) return null;
  return amount != null
    ? `${scheme}:${address}?amount=${amount}`
    : `${scheme}:${address}`;
}

function Header({ expiresAt }: { expiresAt: string | null }) {
  const remaining = useCountdown(expiresAt);
  const expired = remaining != null && remaining <= 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 9px',
          borderRadius: 999,
          background: expired ? 'var(--pm-coral-soft)' : 'var(--pm-amber-soft)',
          color: expired ? 'var(--pm-coral-deep)' : 'var(--pm-amber)',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}
      >
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            background: 'currentColor',
            animation: expired ? undefined : 'pmPulse 1.6s infinite',
          }}
        />
        {expired ? 'Invoice expired' : 'Awaiting payment'}
      </span>
      {remaining != null && !expired && (
        <span
          style={{
            fontSize: 12,
            color: 'var(--pm-ink-3)',
            fontFamily: 'var(--pm-mono)',
          }}
        >
          {formatCountdown(remaining)}
        </span>
      )}
      <style>{`@keyframes pmPulse {
        0% { opacity: 1 }
        50% { opacity: 0.35 }
        100% { opacity: 1 }
      }`}</style>
    </div>
  );
}

function AmountBlock({ result }: { result: CheckoutResponse }) {
  if (result.pay_amount == null) return null;
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: 'var(--pm-ink-4)',
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        Send exactly
      </div>
      <div
        style={{
          fontFamily: 'var(--pm-mono)',
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: -0.4,
          color: 'var(--pm-ink)',
        }}
      >
        {result.pay_amount} {result.pay_currency?.toUpperCase()}
      </div>
    </div>
  );
}

function AddressBlock({ address }: { address: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!address) return null;
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: 'var(--pm-ink-4)',
            fontWeight: 600,
          }}
        >
          To this address
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(address);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              // ignore — most users will scan the QR anyway
            }
          }}
          style={{
            fontSize: 11,
            color: 'var(--pm-ink-3)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {copied ? '✓ copied' : 'copy'}
        </button>
      </div>
      <code
        style={{
          display: 'block',
          padding: '8px 10px',
          background: 'var(--pm-cream-2)',
          border: '1px solid var(--pm-rule)',
          borderRadius: 8,
          fontFamily: 'var(--pm-mono)',
          fontSize: 11.5,
          color: 'var(--pm-ink-2)',
          wordBreak: 'break-all',
          lineHeight: 1.4,
        }}
      >
        {address}
      </code>
    </div>
  );
}

/**
 * Shows the coin symbol + the *exact* network the user must pick in
 * Binance's withdrawal screen. Network mismatch on withdrawal is the #1
 * way crypto users lose money — surfacing this prominently is the single
 * highest-leverage thing we can do for Binance-heavy users.
 */
function NetworkBlock({ currency }: { currency: string | null }) {
  const parsed = parseCryptoCurrency(currency);
  if (!parsed.network) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        border: '1px solid rgba(255,92,44,0.30)',
        background: 'var(--pm-coral-soft)',
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--pm-coral-deep)',
          lineHeight: 1,
          marginTop: 2,
        }}
      >
        !
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 10.5,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: 'var(--pm-coral-deep)',
            fontWeight: 700,
            marginBottom: 2,
          }}
        >
          Network — pick this exactly
        </div>
        <div style={{ fontSize: 13, color: 'var(--pm-ink)', lineHeight: 1.45 }}>
          Send <strong>{parsed.symbol}</strong> on the{' '}
          <strong>{parsed.network.label}</strong> network.
          {parsed.network.binanceSelector && (
            <>
              {' '}
              In Binance, choose{' '}
              <code
                style={{
                  fontFamily: 'var(--pm-mono)',
                  fontSize: 12,
                  background: 'var(--pm-tint-2)',
                  padding: '1px 6px',
                  borderRadius: 4,
                }}
              >
                {parsed.network.binanceSelector}
              </code>
              .
            </>
          )}{' '}
          A different network = lost funds.
        </div>
      </div>
    </div>
  );
}

/**
 * Compact four-step "Withdraw from Binance" cheat sheet. Most of our
 * paying users come from Binance mobile, so showing the flow inline
 * saves a help-desk round trip. Collapsible because power users don't
 * need it.
 */
function BinanceTips({ currency }: { currency: string | null }) {
  const [open, setOpen] = useState(true);
  const parsed = parseCryptoCurrency(currency);
  const selector = parsed.network?.binanceSelector ?? 'the matching network';
  const symbol = parsed.symbol || 'the coin';
  return (
    <div
      style={{
        borderRadius: 10,
        border: '1px solid var(--pm-rule)',
        background: 'var(--pm-paper)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--pm-ink-2)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              background: '#F0B90B',
              color: '#16140F',
              fontSize: 11,
              fontWeight: 800,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            B
          </span>
          Paying from Binance?
        </span>
        <span style={{ fontSize: 11, color: 'var(--pm-ink-4)' }}>
          {open ? 'hide' : 'show'}
        </span>
      </button>
      {open && (
        <ol
          style={{
            margin: 0,
            padding: '0 14px 12px 30px',
            fontSize: 12.5,
            color: 'var(--pm-ink-2)',
            lineHeight: 1.55,
          }}
        >
          <li>
            Open Binance → <strong>Wallet</strong> → <strong>Withdraw</strong> →
            search <strong>{symbol}</strong>.
          </li>
          <li>
            For <strong>Network</strong>, pick{' '}
            <code
              style={{
                fontFamily: 'var(--pm-mono)',
                fontSize: 12,
                background: 'var(--pm-cream-2)',
                padding: '1px 6px',
                borderRadius: 4,
              }}
            >
              {selector}
            </code>
            . Wrong network = funds gone, even if the address looks right.
          </li>
          <li>
            Paste the address above (or scan the QR). Set Amount to the exact{' '}
            {symbol} figure shown.
          </li>
          <li>
            Confirm. Binance usually releases the withdrawal in 2–10 minutes;
            this page flips to <strong>Payment confirmed</strong> on its own.
          </li>
        </ol>
      )}
    </div>
  );
}

function WaitingNotice({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div
      style={{
        padding: '8px 12px',
        background: 'var(--pm-cream-2)',
        borderRadius: 8,
        fontSize: 12,
        color: 'var(--pm-ink-3)',
        lineHeight: 1.5,
      }}
    >
      We&apos;re watching the blockchain. Most coins confirm in 5–30 minutes; this
      page will update automatically.{' '}
      <button
        type="button"
        onClick={onRefresh}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--pm-ink-2)',
          textDecoration: 'underline',
          fontSize: 12,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        Check now
      </button>
    </div>
  );
}

function CryptoPaidView({ sub }: { sub: { tier: string; current_period_end: string | null } }) {
  return (
    <div
      style={{
        padding: '22px 24px',
        borderRadius: 14,
        border: '1px solid rgba(31,107,79,0.30)',
        background: 'var(--pm-emerald-soft)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          background: 'var(--pm-emerald)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <polyline
            points="5 12 10 17 19 7"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div>
        <div
          style={{
            fontFamily: 'var(--pm-serif)',
            fontSize: 18,
            fontWeight: 500,
            color: 'var(--pm-emerald)',
            lineHeight: 1.3,
          }}
        >
          Payment confirmed
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--pm-ink-3)',
            marginTop: 2,
          }}
        >
          You&apos;re on <strong>{sub.tier}</strong> until{' '}
          {sub.current_period_end
            ? new Date(sub.current_period_end).toLocaleDateString()
            : '—'}
          .
        </div>
      </div>
    </div>
  );
}

// ---- helpers ----

function useCountdown(expiresAt: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!expiresAt) return null;
  return Math.max(0, new Date(expiresAt).getTime() - now);
}

function formatCountdown(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')} left`;
}

interface ParsedCurrency {
  symbol: string;
  network: { label: string; binanceSelector: string } | null;
}

// Names match what Binance shows in its "Network" dropdown so the user
// can recognise it character-for-character. Kept tiny on purpose — we
// only need to cover what NOWPayments actually quotes in invoices.
const NETWORK_BY_SUFFIX: Record<string, { label: string; binanceSelector: string }> = {
  trc20: { label: 'Tron (TRC-20)', binanceSelector: 'TRC20' },
  erc20: { label: 'Ethereum (ERC-20)', binanceSelector: 'ERC20' },
  bep20: { label: 'BNB Smart Chain (BEP-20)', binanceSelector: 'BSC (BEP20)' },
  bsc: { label: 'BNB Smart Chain (BEP-20)', binanceSelector: 'BSC (BEP20)' },
  matic: { label: 'Polygon', binanceSelector: 'MATIC (Polygon)' },
  sol: { label: 'Solana', binanceSelector: 'SOL (Solana)' },
  arb: { label: 'Arbitrum One', binanceSelector: 'Arbitrum One' },
  op: { label: 'Optimism', binanceSelector: 'Optimism' },
};

// Coins where the coin IS the network (no suffix to strip).
const STANDALONE_NETWORK: Record<string, { label: string; binanceSelector: string }> = {
  btc: { label: 'Bitcoin', binanceSelector: 'BTC (Bitcoin)' },
  ltc: { label: 'Litecoin', binanceSelector: 'LTC (Litecoin)' },
  eth: { label: 'Ethereum (ERC-20)', binanceSelector: 'ERC20' },
  bnb: { label: 'BNB Smart Chain (BEP-20)', binanceSelector: 'BSC (BEP20)' },
  sol: { label: 'Solana', binanceSelector: 'SOL (Solana)' },
  matic: { label: 'Polygon', binanceSelector: 'MATIC (Polygon)' },
  trx: { label: 'Tron (TRC-20)', binanceSelector: 'TRC20' },
  xrp: { label: 'XRP Ledger', binanceSelector: 'XRP' },
  ada: { label: 'Cardano', binanceSelector: 'ADA (Cardano)' },
  doge: { label: 'Dogecoin', binanceSelector: 'DOGE (Dogecoin)' },
};

/**
 * Split a NOWPayments currency code into its symbol + network. The codes
 * encode the chain as a suffix (`usdttrc20`, `usdcerc20`) for tokens, or
 * are bare coin tickers (`btc`, `eth`) for native assets.
 */
function parseCryptoCurrency(currency: string | null): ParsedCurrency {
  if (!currency) return { symbol: '', network: null };
  const cur = currency.toLowerCase();
  if (STANDALONE_NETWORK[cur]) {
    return { symbol: cur.toUpperCase(), network: STANDALONE_NETWORK[cur] };
  }
  // Longest-suffix-first so `bep20` wins over `bsc` when both could match.
  const suffixes = Object.keys(NETWORK_BY_SUFFIX).sort(
    (a, b) => b.length - a.length,
  );
  for (const suffix of suffixes) {
    if (cur.endsWith(suffix)) {
      const symbol = cur.slice(0, -suffix.length).toUpperCase();
      if (!symbol) continue; // pure-suffix code shouldn't happen, but guard
      return { symbol, network: NETWORK_BY_SUFFIX[suffix] };
    }
  }
  return { symbol: cur.toUpperCase(), network: null };
}
